import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SiteConfigProvider } from "@/context/SiteConfigContext";
import AuthCallback from "@/components/AuthCallback";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import Legal from "@/pages/Legal";
import ProtectedRoute from "@/components/ProtectedRoute";
import SupportWidget from "@/components/SupportWidget";
import TawkWidget from "@/components/TawkWidget";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import Catalog from "@/pages/Catalog";
import AIRecommender from "@/pages/AIRecommender";
import Login from "@/pages/Login";
import StartRequest from "@/pages/StartRequest";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Profile from "@/pages/Profile";
import RequestDetail from "@/pages/RequestDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AboutPage from "@/pages/AboutPage";
import PartnershipPage from "@/pages/PartnershipPage";
import ContactPage from "@/pages/ContactPage";
import MoreServicesPage from "@/pages/MoreServicesPage";
import DigitalStudio from "@/pages/DigitalStudio";
import ServiceDetail from "@/pages/ServiceDetail";
import PaymentGateway from "@/pages/PaymentGateway";
import CountryDetail from "@/pages/CountryDetail";
import ComparePage from "@/pages/ComparePage";
import BankDetail from "@/pages/BankDetail";
import BanksPage from "@/pages/BanksPage";
import BankComparePage from "@/pages/BankComparePage";
import AdminPage from "@/pages/AdminPage";
import UploadLinkPage from "@/pages/UploadLinkPage";
import NotFound from "@/pages/NotFound";
import LangRoute from "@/components/LangRoute";
import { LANGUAGES } from "@/i18n/config";

function RedirectCountry() {
  const { slug } = useParams();
  return <Navigate to={`/company-formation/${slug}`} replace />;
}

// Single source of route definitions — rendered once unprefixed (English) and once per
// language prefix (/vi, /zh-hans, /fr, /es). Keeps components un-duplicated.
const ROUTES = [
  { path: "/", element: <Landing /> },
  { path: "/company-formation", element: <Catalog /> },
  { path: "/services", element: <Navigate to="/company-formation" replace /> },
  { path: "/advisor", element: <AIRecommender /> },
  { path: "/about", element: <AboutPage /> },
  { path: "/partnership", element: <PartnershipPage /> },
  { path: "/partner-program", element: <Navigate to="/partnership" replace /> },
  { path: "/contact", element: <ContactPage /> },
  { path: "/more-services", element: <MoreServicesPage /> },
  { path: "/digital-studio", element: <DigitalStudio /> },
  { path: "/legal/:doc", element: <Legal /> },
  { path: "/service/:slug", element: <ServiceDetail /> },
  { path: "/payment-gateway", element: <PaymentGateway /> },
  { path: "/company-formation/:slug", element: <CountryDetail /> },
  { path: "/country/:slug", element: <RedirectCountry /> },
  { path: "/compare", element: <ComparePage /> },
  { path: "/banks", element: <BanksPage /> },
  { path: "/bank/:slug", element: <BankDetail /> },
  { path: "/bank-compare", element: <BankComparePage /> },
  { path: "/admin", element: <ProtectedRoute><AdminPage /></ProtectedRoute> },
  { path: "/u/:token", element: <UploadLinkPage /> },
  { path: "/login", element: <Login /> },
  { path: "/start", element: <ProtectedRoute><StartRequest /></ProtectedRoute> },
  { path: "/dashboard", element: <ProtectedRoute><Dashboard /></ProtectedRoute> },
  { path: "/orders", element: <ProtectedRoute><Orders /></ProtectedRoute> },
  { path: "/profile", element: <ProtectedRoute><Profile /></ProtectedRoute> },
  { path: "/requests/:id", element: <ProtectedRoute><RequestDetail /></ProtectedRoute> },
  { path: "/payment/success", element: <PaymentSuccess /> },
  { path: "/payment/cancel", element: <PaymentCancel /> },
  { path: "/vara-license", element: <Navigate to="/service/vara" replace /> },
  { path: "/investment-fund", element: <Navigate to="/service/fund" replace /> },
  { path: "/licensing", element: <Navigate to="/more-services#forex" replace /> },
  { path: "/about-2", element: <Navigate to="/about" replace /> },
  { path: "/company-incorporation", element: <Navigate to="/company-formation" replace /> },
  { path: "/product/mercury", element: <Navigate to="/bank/mercury" replace /> },
];

const LANG_PREFIXES = LANGUAGES.filter((l) => l.prefix).map((l) => l.prefix);

function AppRouter() {
  const location = useLocation();
  // Handle the OAuth callback synchronously during render (before ProtectedRoute runs).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <LangRoute>
      <Routes>
        {ROUTES.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}
        {LANG_PREFIXES.flatMap((pfx) =>
          ROUTES.map((r) => (
            <Route key={`${pfx}${r.path}`} path={`/${pfx}${r.path === "/" ? "" : r.path}`} element={r.element} />
          ))
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LangRoute>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <SiteConfigProvider>
          <AuthProvider>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
            <SupportWidget />
            <TawkWidget />
            <CookieConsent />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </SiteConfigProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
